/**
 * Utilities for Jira-style entity ids shown in the UI and URLs (e.g. VD-001,
 * DC-003).
 *
 * The id behind the prefix is opaque. It is a plain number today (`5`) and
 * becomes a uuid
 * (`90f0d6b4-ae66-4a02-b2dc-a50aeabf3389`) when the database moves to
 * Kalligo-shaped tables, so nothing here may treat the part after the prefix
 * as a number unless it is only digits.
 */

const DIGITS_ONLY = /^\d+$/;

/**
 * Format an id with the given prefix. A numeric id is zero-padded to 3 digits
 * (`5` -> `VD-005`); any other id (a uuid) is appended as it is.
 *
 * Idempotent: an id that already carries the prefix is returned unchanged.
 * Not every entity stores a bare id — some API rows hand back ids that are
 * ALREADY "GS-033", so formatting one a second time produced "GS-GS-033".
 * The guard belongs here rather than in each caller remembering which kind it
 * is holding.
 */
export function fmtId(prefix: string, id: string | number): string {
  const raw = String(id);
  if (raw.toUpperCase().startsWith(prefix.toUpperCase() + '-')) return raw;
  return `${prefix}-${DIGITS_ONLY.test(raw) ? raw.padStart(3, '0') : raw}`;
}

/**
 * Parse a URL segment that may be a prefixed id (e.g. "VD-005") or a bare id
 * (e.g. "5", or a uuid). Returns the id without its prefix, or undefined if the
 * input is empty or only a prefix.
 *
 * A numeric tail is normalised ("VD-005" -> "5"). Any other tail — a uuid — is
 * returned whole. It used to run parseInt on every tail, which turned
 * "VD-90f0d6b4-ae66-..." into "90" and silently opened the wrong record.
 *
 * Examples:
 *   parseId('VD', 'VD-005')      -> '5'
 *   parseId('VD', '5')           -> '5'
 *   parseId('VD', 'VD-90f0d6b4-ae66-4a02-b2dc-a50aeabf3389')
 *                                -> '90f0d6b4-ae66-4a02-b2dc-a50aeabf3389'
 *   parseId('VD', undefined)     -> undefined
 */
/**
 * Prefixes that used to label a kind of record and may still sit in bookmarks, e-mails and printed
 * references. They are read like the current one, never written. Aso-ebi orders were FO-012 and are AOS-012.
 */
const RETIRED_PREFIXES: Record<string, readonly string[]> = { AOS: ['FO'] };

export function parseId(prefix: string, raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  const upper = raw.toUpperCase();
  const pfx = [prefix.toUpperCase(), ...(RETIRED_PREFIXES[prefix.toUpperCase()] ?? [])]
    .map(p => p + '-')
    .find(p => upper.startsWith(p));
  if (!pfx) return raw;
  const tail = raw.slice(pfx.length);
  if (!tail) return undefined;
  return DIGITS_ONLY.test(tail) ? String(parseInt(tail, 10)) : tail;
}

/**
 * The record id at the end of a `<base>/<id>` URL path, or null for the bare
 * list route and the `new` form route. Handles numeric and uuid ids alike.
 *
 *   parseIdFromPath('PY', '/payments/record/PY-007', '/payments/record/') -> '7'
 *   parseIdFromPath('PY', '/payments/record/new', '/payments/record/')    -> null
 */
export function parseIdFromPath(prefix: string, pathname: string, base: string): string | null {
  const at = pathname.indexOf(base);
  if (at === -1) return null;
  const segment = pathname.slice(at + base.length).split(/[/?#]/)[0];
  if (!segment || segment.toLowerCase() === 'new') return null;
  return parseId(prefix, segment) ?? null;
}

/**
 * True when `a` and `b` name the same record under `prefix`, whichever of
 * prefixed ("GS-033"), bare numeric ("33") or uuid form each one is in.
 * `parseId` on both sides would do the same job here; this stays a single
 * function so call sites compare ids without re-deriving the canonical form
 * at every comparison.
 */
export function sameId(prefix: string, a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  const canon = (v: string | number | null | undefined): string => {
    if (v == null || v === '') return '';
    return parseId(prefix, String(v)) ?? '';
  };
  const ca = canon(a);
  return ca !== '' && ca === canon(b);
}
